"use client"

import React from "react"
import Link from "next/link"
import { AlertTriangle, RefreshCw, Home } from "lucide-react"
import { Button } from "@/components/ui/button"

interface ErrorPageProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function SoundsError({ error, reset }: ErrorPageProps) {
  React.useEffect(() => {
    import('@sentry/browser').then(({ captureException }) => {
      captureException(error, { tags: { context: 'SoundsErrorBoundary' } })
    }).catch(() => {})
  }, [error])

  return (
    <div className="relative min-h-screen overflow-hidden bg-[var(--tls-canvas)]">
      <div className="relative mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-lg rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[color:color-mix(in_srgb,var(--tls-surface)_94%,transparent)] px-6 py-8 text-center sm:px-8">
          {/* Bounded failure-surface exception keeps the contained ground,
              but the border and ground are ink/hairline, not red. */}
          <div className="mx-auto mb-5 flex size-16 items-center justify-center rounded-[var(--radius-pill)] border border-[color:var(--tls-border-strong)] bg-[var(--tls-surface-elevated)] text-[var(--tls-fg-strong)]">
            <AlertTriangle className="size-8" />
          </div>

          <div className="space-y-2">
            <h1 className="font-display text-2xl text-[var(--tls-fg-strong)]">
              Failed to load sounds
            </h1>
            <p className="text-sm text-[var(--tls-fg-muted)] sm:text-base">
              We couldn&apos;t reach the sound library just now. This is usually
              temporary: try again, or check your connection.
            </p>
          </div>

          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <Button onClick={reset} className="font-display">
              <RefreshCw className="size-4" />
              Try again
            </Button>
            <Button asChild variant="outline" className="font-display">
              <Link href="/">
                <Home className="size-4" />
                Home
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
